import { z } from 'zod';
import { ConnectionRequestSchema } from '@trinity/management-app-client/types/connection-requests';
import { CanonicalWidgetDataSchema } from './dashboard-data.js';

// Connection requests live in management-app-client (the proxy request schema needs them, and this
// package depends on that one, not the reverse). Re-exported so page code has one place to import from.
export {
  HttpConnectionRequestSchema,
  ConnectionRequestSchema,
  CONNECTION_REQUEST_KIND_BY_PROVIDER,
  type ConnectionRequest,
  type ConnectionRequestKind,
} from '@trinity/management-app-client/types/connection-requests';

// ─── Widget Data Source ───────────────────────────────────────────────────────

/**
 * Per-widget data source configuration.
 * - `static`: data is authored inline in the builder and stored as CanonicalWidgetData.
 *   Widget components always read from DashboardDataContext — they never inspect `type`.
 * - `connection`: data is fetched at render time via the host app's backend proxy,
 *   which looks up the data connection by `connectionIdentifier`, resolves its auth,
 *   and sends `request` to it.
 * - `agent` (legacy): data is fetched at render time via the host app's backend proxy,
 *   which looks up the agent by `agentId`, resolves auth, and fetches
 *   `agent.protocol.url + extensionPath`. Superseded by `connection`; kept so existing
 *   pages keep working.
 */
const StaticWidgetDataSourceSchema = z.object({
  type: z.literal('static'),
  /** Authored data stored directly in the config — always CanonicalWidgetData. */
  data: CanonicalWidgetDataSchema,
});

const AgentWidgetDataSourceSchema = z.object({
  type: z.literal('agent'),
  /** FK to the management-api agent record */
  agentId: z.number().int(),
  /** Path appended to the agent's base URL (e.g. "/api/report") */
  extensionPath: z.string(),
  /**
   * Expected response shape for this agent endpoint.
   * Stored at save time (sourced from `getAgentDataExample`) and used by the
   * backend proxy to validate the agent's response before forwarding it to
   * the frontend.
   */
  dataModel: CanonicalWidgetDataSchema,
});

const ConnectionWidgetDataSourceSchema = z.object({
  type: z.literal('connection'),
  /** Identifier of the management-api data connection */
  connectionIdentifier: z.uuid(),
  request: ConnectionRequestSchema,
  /**
   * Expected response shape for this request.
   * Stored at save time (sourced from `getAgentDataExample`), as for `agent`.
   */
  dataModel: CanonicalWidgetDataSchema,
});

// Each variant's type is an interface so declaration emit references it by name instead of inlining
// CanonicalWidgetData into every widget variant; inlined, schemas that embed WidgetConfigSchema (e.g.
// profile-config) exceed the compiler's serialization limit (TS7056). The interfaces extend the inferred
// types rather than restating them, so they match the schemas under strict and non-strict compiler options
// alike (chat-components compiles this file with `strict: false`, where Zod infers every key as optional).
/* eslint-disable @typescript-eslint/no-empty-interface, @typescript-eslint/no-empty-object-type */
export interface StaticWidgetDataSource extends z.infer<typeof StaticWidgetDataSourceSchema> {}
export interface AgentWidgetDataSource extends z.infer<typeof AgentWidgetDataSourceSchema> {}
export interface ConnectionWidgetDataSource extends z.infer<typeof ConnectionWidgetDataSourceSchema> {}
/* eslint-enable @typescript-eslint/no-empty-interface, @typescript-eslint/no-empty-object-type */
export type WidgetDataSource = StaticWidgetDataSource | AgentWidgetDataSource | ConnectionWidgetDataSource;

export const WidgetDataSourceSchema: z.ZodType<WidgetDataSource, WidgetDataSource> = z.discriminatedUnion('type', [
  StaticWidgetDataSourceSchema,
  AgentWidgetDataSourceSchema,
  ConnectionWidgetDataSourceSchema,
]);

// ─── Widget Types ────────────────────────────────────────────────────────────

export const DashboardWidgetTypeEnum = z.enum([
  'chart',
  'text',
  'metric_card',
  'code',
  'rep_dashboard',
  'quote',
  'segment_overview',
  // Patient Journey types — hidden in the default registry
  'pj_media',
  // Saved-widget pointer (any library) — inserted from the palette's library step
  'library_widget',
  // Legacy saved-widget pointers — read-only, upgraded to library_widget on load
  'seg_widget',
  'pj_widget',
]);
export type DashboardWidgetType = z.infer<typeof DashboardWidgetTypeEnum>;

// ─── Chart Widget Config ──────────────────────────────────────────────────────

/** Chart types derived from the QuickChartData discriminated union */
export const DashboardChartTypeEnum = z.enum([
  'bar',
  'stacked-bar',
  'pie',
  'line',
  'multiple-line',
  'multiple-clustered-bar',
  'multiple-stacked-bar',
  'data-table',
  'kpi-widget',
  'progress-bar-widget',
  'semi-circle-progress-widget',
]);
export type DashboardChartType = z.infer<typeof DashboardChartTypeEnum>;

/** A single row of static chart data entered manually in the builder sidebar */
export const ChartStaticRowSchema = z.object({
  category: z.string(),
  value: z.union([z.string(), z.number()]),
});
export type ChartStaticRow = z.infer<typeof ChartStaticRowSchema>;

/** A named data series for multi-series chart types (clustered/stacked bar) */
export const StaticSeriesSchema = z.object({
  name: z.string(),
  rows: z.array(ChartStaticRowSchema),
});
export type StaticSeries = z.infer<typeof StaticSeriesSchema>;

export const ChartWidgetConfigSchema = z.object({
  title: z.string(),
  chartType: DashboardChartTypeEnum,
  chartThemeId: z.number().int().optional(),
  colors: z.array(z.string()).optional(),
  colorOverrides: z.record(z.string(), z.string()).optional(),
  labelOverrides: z.record(z.string(), z.string()).optional(),
  /** X-axis label shown on the chart */
  xAxisLabel: z.string().optional(),
  /** Y-axis label shown on the chart */
  yAxisLabel: z.string().optional(),
  // Data (staticRows, staticSeries) has moved to WidgetConfig.dataSource.data (CanonicalWidgetData)
});
export type ChartWidgetConfig = z.infer<typeof ChartWidgetConfigSchema>;

// ─── Text Widget Config ───────────────────────────────────────────────────────

export const TextWidgetConfigSchema = z.object({
  title: z.string(),
  /**
   * Display setting: whether to render as rich text (Remirror) or markdown.
   * Defaults to 'richtext' for backwards compatibility.
   * The actual content string lives in WidgetConfig.dataSource.data.
   */
  textMode: z.enum(['richtext', 'markdown']).optional(),
  // content / markdownContent have moved to WidgetConfig.dataSource.data (CanonicalWidgetData)
});
export type TextWidgetConfig = z.infer<typeof TextWidgetConfigSchema>;

// ─── KPI Item ─────────────────────────────────────────────────────────────────

/** A single KPI entry used in metric card widgets */
export const KpiItemSchema = z.object({
  label: z.string(),
  value: z.string(),
  unit: z.string(),
});
export type KpiItem = z.infer<typeof KpiItemSchema>;

// ─── Metric Card Widget Config ────────────────────────────────────────────────

export const MetricCardWidgetConfigSchema = z.object({
  title: z.string(),
  /** Background colour for the metric card (hex string). Defaults to transparent when absent. */
  backgroundColor: z.string().optional(),
  // kpis have moved to WidgetConfig.dataSource.data (CanonicalWidgetData metric_card shape)
});
export type MetricCardWidgetConfig = z.infer<typeof MetricCardWidgetConfigSchema>;

// ─── Code Widget Config ───────────────────────────────────────────────────────

export const CodeWidgetConfigSchema = z.object({
  title: z.string(),
  jsx: z.string(),
  presetType: z.string().optional(),
});
export type CodeWidgetConfig = z.infer<typeof CodeWidgetConfigSchema>;

// ─── Rep Dashboard Widget Config ──────────────────────────────────────────────

export const RepDashboardVisibleSectionsSchema = z.object({
  summary: z.boolean().optional(),
  kpis: z.boolean().optional(),
  opportunities: z.boolean().optional(),
  table: z.boolean().optional(),
});

export const RepDashboardWidgetConfigSchema = z.object({
  title: z.string(),
  /** Which sections to display. All default to true when omitted. */
  visibleSections: RepDashboardVisibleSectionsSchema.optional(),
  /** Background color for collapsible section headers. Defaults to #001845. */
  headerColor: z.string().optional(),
  /** Accent color for values and icon backgrounds. Defaults to #FF5757. */
  accentColor: z.string().optional(),
  /** Default number of table rows per page. Defaults to 5. */
  defaultRowsPerPage: z.number().int().optional(),
});
export type RepDashboardWidgetConfig = z.infer<typeof RepDashboardWidgetConfigSchema>;

// ─── Quote Widget Config ─────────────────────────────────────────────────────

export const QuoteWidgetConfigSchema = z.object({
  title: z.string(),
  /** Optional speaker name displayed below the quote */
  speaker: z.string().optional(),
});
export type QuoteWidgetConfig = z.infer<typeof QuoteWidgetConfigSchema>;

// ─── PJ Media Widget Config ───────────────────────────────────────────────────

/**
 * PJ media widget — reference to a project widget by id.
 * Content is never stored inline; resolved at render time from the project widgets map.
 * Matches existing behaviour: a deleted widget renders nothing, an edited widget reflects immediately.
 */
/** Legacy: kept only so stored `pj_media` widgets validate (widgetId may be 0 there). */
export const PjMediaWidgetConfigSchema = z.object({
  title: z.string(),
  /** FK to the project `widgets` table row */
  widgetId: z.number().int(),
});
export type PjMediaWidgetConfig = z.infer<typeof PjMediaWidgetConfigSchema>;

// ─── Library Widget Config ────────────────────────────────────────────────────

/**
 * Which saved-widget library a `library_widget` points into.
 * - `project_library`: the project's widgets table (every saved-widget pointer today).
 * - `library`: an app-wide library; reserved, no app supplies one yet.
 */
export const WidgetLibrarySourceSchema = z.enum(['project_library', 'library']);
export type WidgetLibrarySource = z.infer<typeof WidgetLibrarySourceSchema>;

/** Pointer to a saved widget, resolved at render time by the host app's WidgetLibrary for `source`. */
export const LibraryWidgetConfigSchema = z.object({
  title: z.string(),
  widgetId: z.number().int().positive(),
  /** Missing means `project_library`. */
  source: WidgetLibrarySourceSchema.optional(),
});
export type LibraryWidgetConfig = z.infer<typeof LibraryWidgetConfigSchema>;

export type WidgetRef = { source: WidgetLibrarySource; widgetId: number };

// ─── Widget Config (discriminated union) ─────────────────────────────────────

/**
 * Optional source attribution for any widget: where its data came from, and the methodology behind it.
 *
 * Stored in page configs, so changes must be additive: new fields optional, existing ones never
 * removed, renamed or made required. A reader that does not know a field drops it on parse, so a page
 * saved from an older client loses fields added later; add a field here before anything writes it.
 */
export const SourceAttributionSchema = z
  .object({
    /** Human-readable label, e.g. "Claims Data Q1-Q4 2024" */
    label: z.string(),
    /** Source type, e.g. "file", "api", "ai-generated" */
    type: z.string().optional(),
    /** Respondents or records behind the figures (the "n="). */
    sampleSize: z.number().int().min(0).optional(),
    /** Who was asked or measured, e.g. "Oncologists, US". Shown as written. */
    audience: z.string().optional(),
    /** When the data was collected, e.g. "Mar–Apr 2026". Shown as written. */
    fieldDates: z.string().optional(),
    /** Margin of error, e.g. "±3.1% at 95% confidence". Shown as written. */
    marginOfError: z.string().optional(),
  })
  .optional();

export const WidgetConfigSchema = z.discriminatedUnion('type', [
  z.object({
    id: z.string(),
    type: z.literal('chart'),
    config: ChartWidgetConfigSchema,
    dataSource: WidgetDataSourceSchema.optional(),
    sourceAttribution: SourceAttributionSchema,
  }),
  z.object({
    id: z.string(),
    type: z.literal('text'),
    config: TextWidgetConfigSchema,
    dataSource: WidgetDataSourceSchema.optional(),
    sourceAttribution: SourceAttributionSchema,
  }),
  z.object({
    id: z.string(),
    type: z.literal('metric_card'),
    config: MetricCardWidgetConfigSchema,
    dataSource: WidgetDataSourceSchema.optional(),
    sourceAttribution: SourceAttributionSchema,
  }),
  z.object({
    id: z.string(),
    type: z.literal('code'),
    config: CodeWidgetConfigSchema,
    dataSource: WidgetDataSourceSchema.optional(),
    sourceAttribution: SourceAttributionSchema,
  }),
  z.object({
    id: z.string(),
    type: z.literal('rep_dashboard'),
    config: RepDashboardWidgetConfigSchema,
    dataSource: WidgetDataSourceSchema.optional(),
    sourceAttribution: SourceAttributionSchema,
  }),
  z.object({
    id: z.string(),
    type: z.literal('quote'),
    config: QuoteWidgetConfigSchema,
    dataSource: WidgetDataSourceSchema.optional(),
    sourceAttribution: SourceAttributionSchema,
  }),
  z.object({
    id: z.string(),
    type: z.literal('segment_overview'),
    config: z.object({ title: z.string().optional() }),
    dataSource: WidgetDataSourceSchema.optional(),
    sourceAttribution: SourceAttributionSchema,
  }),
  z.object({
    id: z.string(),
    type: z.literal('library_widget'),
    config: LibraryWidgetConfigSchema,
    dataSource: WidgetDataSourceSchema.optional(),
    sourceAttribution: SourceAttributionSchema,
  }),
  // Legacy saved-widget pointers: still accepted so stored pages validate, never written. Loading a
  // page rewrites them to `library_widget` (upgradeLegacyLibraryRefs), so the next save drops them.
  z.object({
    id: z.string(),
    type: z.literal('pj_media'),
    config: PjMediaWidgetConfigSchema,
    dataSource: WidgetDataSourceSchema.optional(),
    sourceAttribution: SourceAttributionSchema,
  }),
  z.object({
    id: z.string(),
    type: z.enum(['seg_widget', 'pj_widget']),
    config: LibraryWidgetConfigSchema,
    dataSource: WidgetDataSourceSchema.optional(),
    sourceAttribution: SourceAttributionSchema,
  }),
]);

/** Source attribution shape (optional object or undefined), derived from its schema. */
export type SourceAttribution = z.infer<typeof SourceAttributionSchema>;

export type WidgetConfig = z.infer<typeof WidgetConfigSchema>;

// ─── WidgetSpecificConfig union ─────────────────────────────────────────────

/**
 * Union of every widget-specific config type.
 * Use this as the constraint for generic `TConfig` parameters (e.g. in
 * `WidgetRegistryEntry<TConfig>`) so callers are restricted to real widget
 * configs rather than arbitrary objects.
 */
export type WidgetSpecificConfig =
  | ChartWidgetConfig
  | TextWidgetConfig
  | MetricCardWidgetConfig
  | CodeWidgetConfig
  | RepDashboardWidgetConfig
  | QuoteWidgetConfig
  | PjMediaWidgetConfig
  | LibraryWidgetConfig;

// ─── Block ────────────────────────────────────────────────────────────────────

/**
 * A block is a container for one optional widget.
 * Blocks are ordered in an array; x/y are derived at render time by row-packing.
 * Height is always 1 row; width (w) is 1–N columns where N is the page's outerCols.
 */
/** A slot/column position within a block (1-indexed). Range depends on the block's column width. */
export type Slot = number;

/** Row number (as a string key) → row height in px. */
export const RowHeightsSchema = z.record(z.string().regex(/^[1-9]\d*$/), z.number().int().min(24).max(4000));
export type RowHeights = z.infer<typeof RowHeightsSchema>;

/** A widget placed at a specific slot inside a block or section, with a column span */
export const BlockWidgetSchema = z.object({
  /** 1-indexed row within the block (row 1 = first row). Defaults to 1 for legacy data. */
  row: z.number().int().min(1).default(1),
  slot: z.number().int().min(1).max(10),
  span: z.number().int().min(1).max(10),
  /** Rows this widget covers (default 1). Widgets beside a taller one can stack in the rows it covers. */
  rowSpan: z.number().int().min(1).max(12).optional(),
  widget: WidgetConfigSchema,
});
export type BlockWidget = z.infer<typeof BlockWidgetSchema>;

// ─── Section ──────────────────────────────────────────────────────────────────

/**
 * A section is a sub-block that lives at a (row, slot, span) position inside a
 * parent block. It has its own independent widget grid (same row/slot/span
 * system as a block). Sections cannot hold other sections — only widgets.
 */
export const SectionConfigSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** When true, the section title is hidden in the rendered dashboard (compact strip style in builder). */
  hideTitle: z.boolean().optional(),
  /** When true, the section can be collapsed/expanded in the rendered dashboard (accordion-style). */
  collapsible: z.boolean().optional(),
  /** When true AND collapsible, the section starts collapsed (user can expand). */
  defaultCollapsed: z.boolean().optional(),
  /** Explicit minimum inner row count set by the drag-resize handle. Auto-grows from content when not set. */
  minInnerRows: z.number().int().min(1).optional(),
  /**
   * Side-by-side widget slots inside the section, when they differ from its span in the block (a
   * patient journey cell's slots follow its column width). Defaults to the span.
   */
  columns: z.number().int().min(1).max(10).optional(),
  /** Rows fit their content (the default when unset). `false` keeps each row at least the fixed row unit. */
  autoHeight: z.boolean().optional(),
  /** Slots size to their widest widget's content instead of splitting the width equally. */
  autoWidth: z.boolean().optional(),
  /** Height floor in px. */
  minHeight: z.number().int().min(0).max(4000).optional(),
  /** Height ceiling in px; content beyond it scrolls. */
  maxHeight: z.number().int().min(0).max(4000).optional(),
  /** Auto-height only: rows set to a height in px by dragging, keyed by row number. Other rows fit their content. */
  rowHeights: RowHeightsSchema.optional(),
  /** Widgets placed inside this section */
  widgets: z.array(BlockWidgetSchema).default([]),
  /** Opaque metadata (e.g. tag info) preserved across saves. */
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type SectionConfig = z.infer<typeof SectionConfigSchema>;

// ─── Block Items (discriminated union) ───────────────────────────────────────

/** A widget item placed directly inside a block */
export const BlockWidgetItemSchema = z.object({
  type: z.literal('widget'),
  row: z.number().int().min(1).default(1),
  slot: z.number().int().min(1).max(10),
  span: z.number().int().min(1).max(10),
  /**
   * Number of block rows this widget covers vertically. Default 1; higher values make the widget
   * taller and leave the rows beside it free for widgets stacked next to it. Not capped here so pages
   * saved before the builder's own 12-row limit still load; the builder clamps what it writes.
   */
  rowSpan: z.number().int().min(1).optional(),
  widget: WidgetConfigSchema,
});
export type BlockWidgetItem = z.infer<typeof BlockWidgetItemSchema>;

/** A section item placed directly inside a block */
export const BlockSectionItemSchema = z.object({
  type: z.literal('section'),
  row: z.number().int().min(1).default(1),
  slot: z.number().int().min(1).max(10),
  span: z.number().int().min(1).max(10),
  section: SectionConfigSchema,
});
export type BlockSectionItem = z.infer<typeof BlockSectionItemSchema>;

/** A discriminated union of everything that can be placed at a (row, slot) inside a block */
export const BlockItemSchema = z.discriminatedUnion('type', [BlockWidgetItemSchema, BlockSectionItemSchema]);
export type BlockItem = z.infer<typeof BlockItemSchema>;

export const BlockConfigSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** When true, the block title is hidden in the rendered dashboard (shows in section-title style in the builder) */
  hideTitle: z.boolean().optional(),
  /** Number of outer grid columns the block spans. Max is the page's outerCols (absolute cap 10). */
  w: z.number().int().min(1).max(10).default(3),
  /**
   * Starting column of the block in the outer grid (0-indexed).
   * Persisted after the user drags a block horizontally. Defaults to 0 when absent.
   */
  x: z.number().int().min(0).max(9).optional(),
  /**
   * Minimum number of rows shown (user-controlled block height).
   * The actual row count is max(minRows, derived-from-items).
   */
  minRows: z.number().int().min(1).optional(),
  /** Rows fit their content (the default when unset). `false` keeps each row at least the fixed row unit. */
  autoHeight: z.boolean().optional(),
  /** Height floor in px. */
  minHeight: z.number().int().min(0).max(4000).optional(),
  /** Height ceiling in px; content beyond it scrolls. */
  maxHeight: z.number().int().min(0).max(4000).optional(),
  /** Auto-height only: rows set to a height in px by dragging, keyed by row number. Other rows fit their content. */
  rowHeights: RowHeightsSchema.optional(),
  /** When true, the block can be collapsed/expanded in the rendered dashboard (accordion-style). */
  collapsible: z.boolean().optional(),
  /** When true AND collapsible, the block starts collapsed (user can expand). */
  defaultCollapsed: z.boolean().optional(),
  /** Items (widgets or sections) placed in the block's column slots */
  items: z.array(BlockItemSchema).default([]),
  /** Opaque metadata preserved across saves (e.g. the segment tag a block was created for). */
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type BlockConfig = z.infer<typeof BlockConfigSchema>;

// ─── Custom Theme ─────────────────────────────────────────────────────────────

/**
 * A user-defined named colour palette stored directly in the dashboard config.
 * IDs are assigned by the client (Date.now()) and are scoped to the page — they
 * never collide with DB-assigned branding palette IDs.
 */
export const CustomThemeSchema = z.object({
  /** Client-assigned integer ID (e.g. Date.now()). Unique within a DashboardConfig. */
  id: z.number().int(),
  name: z.string(),
  /** Ordered hex colour strings, between 5 and 25 entries. */
  colors: z.array(z.string()).min(5).max(25),
});
export type CustomTheme = z.infer<typeof CustomThemeSchema>;

// ─── Page Data Settings ───────────────────────────────────────────────────────

/**
 * Page-level data settings. Neither field changes how a saved widget resolves its data:
 * renderers outside the management builder ignore them.
 */
/** The brand and application a request is made in. Always a pair: a user's role is per application, per brand. */
export const PreviewScopeSchema = z.object({
  brandIdentifier: z.string(),
  applicationIdentifier: z.string(),
});
export type PreviewScope = z.infer<typeof PreviewScopeSchema>;

export const PageDataSettingsSchema = z.object({
  /** Copied into a widget's `connection` source when the widget is created. Never read at render time. */
  defaultConnectionIdentifier: z.uuid().optional(),
  /**
   * The brand and application the management builder and its preview fetch data as. It has to be a pair the
   * editor has themselves: chat-api verifies it on every request, like any other scoped request.
   */
  previewScope: PreviewScopeSchema.optional(),
});
export type PageDataSettings = z.infer<typeof PageDataSettingsSchema>;

// ─── Dashboard Config (root) ──────────────────────────────────────────────────

export const DashboardConfigSchema = z.object({
  /** When true, the dashboard title header is hidden in the rendered view (compact in builder). */
  hideTitle: z.boolean().optional(),
  /**
   * Number of columns in the outer RGL page grid. Defaults to 3 (backward-compatible).
   * Block widths (w) and widget slots/spans are bounded by this value at each nesting level.
   */
  outerCols: z.number().int().min(1).max(12).optional(),
  /** Ordered list of blocks. y positions are derived from column-height packing; x is persisted per block. */
  blocks: z.array(BlockConfigSchema),
  /**
   * User-defined named colour palettes for this page. Stored inline so they
   * persist automatically with every page version save. Selected via
   * ChartWidgetConfig.chartThemeId (matched by id).
   */
  customThemes: z.array(CustomThemeSchema).optional(),
  /**
   * Opaque metadata preserved across saves.
   * PJ grid pages store `{ pjStageTagIds: number[] }` here so the sync
   * function can detect stage reorders/additions/removals without a DB query.
   */
  metadata: z.record(z.string(), z.unknown()).optional(),
  /** Page-level data settings, edited in the management page builder. */
  dataSettings: PageDataSettingsSchema.optional(),
});
export type DashboardConfig = z.infer<typeof DashboardConfigSchema>;

// ─── Page versions ────────────────────────────────────────────────────────────

/** One saved version of a page, as listed by any builder's version history. */
export const PageVersionSummarySchema = z.object({
  id: z.number(),
  versionNumber: z.number(),
  /** A Date in the backend services; an ISO string once serialised over HTTP. */
  createdOn: z.union([z.string(), z.date()]),
  isCurrent: z.boolean(),
  description: z.string().nullable().optional(),
});
export type PageVersionSummary = z.infer<typeof PageVersionSummarySchema>;
